import { getGatewayProductFamily } from './gatewayProductLabel';

/** KGateway-only MQTT admin commands (BLE scanning, beacon types, HTTP upload, etc.). */
const KGATEWAY_COMMAND_IDS = [
  'get_status',
  'set_upload_interval',
  'set_rssi_filter',
  'set_mac_filter',
  'set_name_filter',
  'set_beacon_types',
  'set_duplicate_filter',
  'reboot',
  'factory_reset',
  'change_password',
  'update_ble_config',
  'update_network_config',
  'update_mqtt_config',
  'update_http_config',
  'configure_ap',
  'update_firmware',
];

/** LGateway + MGateway (ESP) — MQTT, timing, filters, network, OTA via release schedule in UI. */
const ESP_COMMAND_IDS = [
  'get_status',
  'set_upload_interval',
  'set_rssi_filter',
  'set_mac_filter',
  'update_mqtt_config',
  'update_network_config',
  'reboot',
  'factory_reset',
  'change_password',
];

const KGATEWAY_SETTINGS_TABS = [
  { id: 'commands', label: 'Commands' },
  { id: 'config', label: 'Configuration' },
];

const ESP_SETTINGS_TABS = [
  { id: 'config', label: 'Device configuration' },
  { id: 'commands', label: 'Remote commands' },
];

/**
 * UI + command capabilities aligned with native ChillVerify screens:
 * - KGateway → GatewayAdmin (MQTT remote admin + alive heartbeat)
 * - LGateway / MGateway → LilyGoGatewayAdmin (getCfg device config + ESP commands)
 *
 * @param {object|null|undefined} gateway
 */
export function getGatewayAdminCapabilities(gateway) {
  const family = gateway ? getGatewayProductFamily(gateway) : null;
  const isKGateway = family === 'KGateway';
  const isLGateway = family === 'LGateway';
  const isMGateway = family === 'MGateway';
  const isEsp = isLGateway || isMGateway;

  return {
    family,
    isKGateway,
    isLGateway,
    isMGateway,
    isEsp,
    /** Native screen name (React Native). */
    nativeScreenName: isKGateway ? 'GatewayAdmin' : isEsp ? 'LilyGoGatewayAdmin' : 'GatewayAdmin',
    commandIds: isKGateway ? KGATEWAY_COMMAND_IDS : ESP_COMMAND_IDS,
    settingsTabs: isKGateway ? KGATEWAY_SETTINGS_TABS : ESP_SETTINGS_TABS,
    defaultSettingsTab: isKGateway ? 'commands' : 'config',
    usesAliveHeartbeat: isKGateway,
    showMessagesTab: isKGateway,
    showHistoryTab: isKGateway,
    showProvisioningConfigNote: isKGateway,
    configTabTitle: isKGateway ? 'KGateway configuration' : `${family || 'Gateway'} configuration`,
    configTabSubtitle: isKGateway
      ? 'Live heartbeat fields and last saved configuration (native GatewayAdmin)'
      : 'Current settings from MQTT getCfg or last saved copy (native LilyGoGatewayAdmin)',
    adminTitle: isKGateway ? 'KGateway admin' : `${family || 'Gateway'} admin`,
    settingsPageTitle: isKGateway
      ? 'KGateway admin'
      : `${family || 'Gateway'} admin`,
    settingsStatusHint: isKGateway
      ? 'Connect for heartbeat data and KBeacon MQTT admin commands'
      : `Connect to send ${family} MQTT commands (filters, timing, OTA)`,
    settingsDescription: isKGateway
      ? 'Scanning filters, beacon types, reboot, factory reset, and OTA — same as the app GatewayAdmin screen.'
      : isLGateway
        ? 'MQTT filters, upload timing, OTA, and broker settings. Network: SoftAP Wi‑Fi or LTE (LilyGoCommissionScreen).'
        : isMGateway
          ? 'MQTT filters, upload timing, OTA, and broker settings. Network: SoftAP Wi‑Fi only (LilyGoCommissionScreen).'
          : 'MQTT admin for this gateway.',
    networkHint: isLGateway
      ? 'Wi‑Fi / LTE provisioning: Configure network (SoftAP)'
      : isMGateway
        ? 'Wi‑Fi provisioning: Configure network (SoftAP)'
        : null,
    supportsLteCommission: isLGateway,
  };
}
